import axios from "../axios/axios";

/**
 * Rate interface mirroring backend entity
 */
export interface Rate {
  id?: string;
  authCode?: string;
  modifier?: string;
  perType?: string;
  currency?: string;
  rateBilled?: number;
  rateAgreed?: number;
  insuranceProviderId?: string;
  employeeId?: string;
  employeeRate?: string;
  effectiveDate?: string;
  expirationDate?: string | null;
  isActive: boolean;
}

/**
 * Get all rates with optional filtering
 * @param filters Optional query parameters for filtering rates
 * @returns Promise with rates data
 */
export const getRates = async (
  filters?: Record<string, any>
): Promise<{ result: Rate[]; count: number }> => {
  try {
    const queryParams = filters
      ? new URLSearchParams(filters as any).toString()
      : "";
    const endpoint = queryParams ? `/rates?${queryParams}` : "/rates";
    const { data } = await axios.get(endpoint);
    return data;
  } catch (e: any) {
    throw e?.response?.data;
  }
};

/**
 * Get only active rates (current date falls within effective and expiration dates)
 * @returns Promise with active rates data
 */
export const getActiveRates = async () => {
  try {
    const { data } = await axios.get("/rates/active");
    return data;
  } catch (e: any) {
    throw e?.response?.data;
  }
};

/**
 * Get a specific rate by ID
 * @param id Rate ID
 * @returns Promise with rate data
 */
export const getRateById = async (id: string) => {
  try {
    const { data } = await axios.get(`/rates/${id}`);
    return data as Rate;
  } catch (e: any) {
    throw e?.response?.data;
  }
};

/**
 * Create a new rate
 * @param payload Rate data to create
 * @returns Promise with created rate data
 */
export const createRate = async (payload: Partial<Rate>) => {
  try {
    const { data } = await axios.post("/rates", payload);
    return data;
  } catch (e: any) {
    throw e?.response?.data;
  }
};

/**
 * Update an existing rate
 * @param id Rate ID to update
 * @param payload Updated rate data
 * @returns Promise with updated rate data
 */
export const updateRate = async (id: string, payload: Partial<Rate>) => {
  try {
    const { data } = await axios.put(`/rates/${id}`, payload);
    return data;
  } catch (e: any) {
    throw e?.response?.data;
  }
};

/**
 * Delete a rate
 * @param id Rate ID to delete
 * @returns Promise with deletion result
 */
// export const deleteRate = async (id: string) => {
// 	try {
// 		const { data } = await axios.delete(`/rates/${id}`);
// 		return data;
// 	} catch (e: any) {
// 		throw e?.response?.data;
// 	}
// };

const ratesService = {
  getRates,
  getActiveRates,
  getRateById,
  createRate,
  updateRate,
  // deleteRate,
};

export default ratesService;
